import { userAccountStore } from '$lib/stores/auth-store';
import { get } from 'svelte/store';
import { createEnum } from './utils';

/**
 * All the defined features that should be locked/unlocked based on permissions. Features != Permissions.
 */
export const featureTypes = createEnum({
	// MASTPOINT
	ACCESS_MASTPOINT: 'ACCESS_MASTPOINT',
	ACCESS_MASTPOINT_ADVANCED: 'ACCESS_MASTPOINT_ADVANCED',
	ACCESS_SPAN_BY_MASTPOINT: 'ACCESS_SPAN_BY_MASTPOINT',
	ACCESS_SPAN_BY_MASTPOINT_ADVANCED: 'ACCESS_SPAN_BY_MASTPOINT_ADVANCED',
	EDIT_MASTPOINT_AND_POLE_POSITIONS: 'EDIT_MASTPOINT_AND_POLE_POSITIONS',
	EDIT_MASTPOINT: 'EDIT_MASTPOINT',
	ACCESS_MASTPOINT_FILES: 'ACCESS_MASTPOINT_FILES',
	UPLOAD_MASTPOINT_FILES: 'UPLOAD_MASTPOINT_FILES',
	// PLAN
	PLANS_ACCESS: 'PLANS_ACCESS',
	EXPORT_PLAN: 'EXPORT_PLAN',
	PLAN_PUBLISH: 'PLAN_PUBLISH',
	PLAN_REPORT: 'PLAN_REPORT',
	PLAN_READ_WRITE: 'PLAN_READ_WRITE',
	PROJECT_READ_WRITE: 'PROJECT_READ_WRITE',
	READ_PLAN_CZML: 'READ_PLAN_CZML',

	// LINES
	LINE_TOOL: 'LINE_TOOL',
	LINE_CALCULATIONS: 'LINE_CALCULATIONS',
	LINE_CAPACITY_REPORT: 'LINE_CAPACITY_REPORT',
	TELEKOM_ACCESS: 'TELEKOM_ACCESS',
	LOW_VOLTAGE_ACCESS: 'LOW_VOLTAGE_ACCESS',
	HIGH_VOLTAGE_ACCESS: 'HIGH_VOLTAGE_ACCESS',
	POLYGON_ORGANIZE_LINES: 'POLYGON_ORGANIZE_LINES',
	ADD_MAIN_COMPANY_LINES: 'ADD_MAIN_COMPANY_LINES',
	// TOOLS
	TERRAIN_PROFILE_TOOL: 'TERRAIN_PROFILE_TOOL',
	SEARCH_TOOL: 'SEARCH_TOOL',
	POLYGON_CAPACITY_REPORT_TOOL: 'POLYGON_CAPACITY_REPORT_TOOL',
	DISTANCE_ANALYSIS_TOOL: 'DISTANCE_ANALYSIS_TOOL',
	// MAINTENANCE
	VEDLIKEHOLD_ACCESS: 'VEDLIKEHOLD_ACCESS',
	VEDLIKEHOLD_CREATE_TEMPLATE: 'VEDLIKEHOLD_CREATE_TEMPLATE',
	CHECKLIST_ACCESS: 'CHECKLIST_ACCESS',
	// TRENCH
	UNDERGROUND: 'UNDERGROUND',
	// ENTITIES
	SMARTGIS_ANALYSER: 'SMARTGIS_ANALYSER',
	SUPPORT_INFORMATION: 'SUPPORT_INFORMATION',
	AVIATION_OBSTACLES: 'AVIATION_OBSTACLES',
	// POINT CLOUDS
	POINTCLOUD_VIEW: 'POINTCLOUD_VIEW',
	POINTCLOUD_UPLOAD: 'POINTCLOUD_UPLOAD',
	POINTCLOUD_DELETE: 'POINTCLOUD_DELETE',
	POINTCLOUD_EDIT: 'POINTCLOUD_EDIT',
	DSSMODULE: 'DSSMODULE',
	IMAGEMODULE: 'IMAGEMODULE',
	UPLOAD_IMAGES: 'UPLOAD_IMAGES',
	UPDATE_IMAGES: 'UPDATE_IMAGES',
	DELETE_IMAGES: 'DELETE_IMAGES'
});

export const PermissionService = {
	/**
	 * This function checks a feature against user permissions. If the user has access to that feature, it returns true. Otherwise false.
	 * We use features instead of permissions directly, as a feature can be available or partly available based on an array of permissions.
	 *
	 * @param feature The feature to check on.
	 * @returns boolean
	 */
	can: (feature: keyof typeof featureTypes): boolean => {
		const userStore = get(userAccountStore);
		if (!userStore || !userStore.permissions?.length) return false;
		// SuperAdmin bypass intentionally disabled: it made permission gating
		// untestable (every check passed regardless of assigned permissions).
		// if (get(userRole) === 'SuperAdmin') return true;

		const permissions = userStore.permissions;

		/*
		Suggestion to use a mapping object instead of switch for performance reasons. Added from another branch as a reminder.
		const permissionMap = {
			ACCESS_MASTPOINT: 'infrastructure:access_mastpoint',
			// Other mappings...
		  };
		return permissions.includes(permissionMap[feature]);
		*/

		switch (feature) {
			case 'UNDERGROUND':
				return permissions.includes('underground:minimal');
			case 'ACCESS_MASTPOINT':
			case 'ACCESS_SPAN_BY_MASTPOINT':
				return permissions.includes('infrastructure:access_mastpoint');
			case 'ACCESS_MASTPOINT_ADVANCED':
			case 'ACCESS_SPAN_BY_MASTPOINT_ADVANCED':
				return permissions.includes('infrastructure:access_mastpoint_advanced');
			case 'ACCESS_MASTPOINT_FILES':
				return permissions.includes('infrastructure:read_mastpoint_file');
			case 'UPLOAD_MASTPOINT_FILES':
				return permissions.includes('infrastructure:write_mastpoint_file');
			case 'EDIT_MASTPOINT':
				return permissions.includes('plan:write_mastpoint');
			case 'EDIT_MASTPOINT_AND_POLE_POSITIONS':
				return permissions.includes('plan:write_mastpoint_position');
			case 'PLANS_ACCESS':
				return permissions.includes('plan:minimal');
			case 'TERRAIN_PROFILE_TOOL':
				return permissions?.includes('plan:terrainprofile_tool');
			case 'DISTANCE_ANALYSIS_TOOL':
				return permissions?.includes('plan:distance_analysis_tool');
			case 'POLYGON_CAPACITY_REPORT_TOOL':
				return permissions.includes('infrastructure:polygon_tool');
			case 'POLYGON_ORGANIZE_LINES':
				return permissions.includes('plan:update_line_offsets');
			case 'EXPORT_PLAN':
				return permissions.includes('plan:publish');
			case 'PLAN_REPORT':
				return permissions.includes('plan:report');
			case 'SMARTGIS_ANALYSER':
				return permissions.includes('plan:read_pole_calculations');
			case 'LINE_CALCULATIONS':
				return permissions.includes('plan:read_line_calculations');
			case 'LINE_CAPACITY_REPORT':
				return permissions.includes('plan:capacity_report');
			case 'SUPPORT_INFORMATION':
				return permissions.includes('plan:read_support_information');
			case 'VEDLIKEHOLD_ACCESS':
				return permissions.includes('maintenance:minimal');
			case 'VEDLIKEHOLD_CREATE_TEMPLATE':
				return permissions.includes('maintenance:create_template');
			case 'LINE_TOOL':
				return (
					permissions.includes('plan:write_lines') ||
					permissions.includes('plan:write_lines_high_voltage') ||
					permissions.includes('line_coop:minimal')
				);
			case 'LOW_VOLTAGE_ACCESS':
				return permissions.includes('plan:write_lines');
			case 'HIGH_VOLTAGE_ACCESS':
				return permissions.includes('plan:write_lines_high_voltage');
			case 'TELEKOM_ACCESS':
				return permissions.includes('line_coop:minimal');
			case 'AVIATION_OBSTACLES':
				return permissions.includes('aviation_obstacle:minimal');
			case 'SEARCH_TOOL':
				return permissions.includes('infrastructure:search_tool');
			case 'PLAN_READ_WRITE':
				return permissions.includes('plan:create');
			case 'PROJECT_READ_WRITE':
				return permissions.includes('plan:project_admin');
			case 'ADD_MAIN_COMPANY_LINES':
				return permissions.includes('plan:write_lines_company');
			case 'READ_PLAN_CZML':
				return permissions.includes('plan:read_infrastructure_entities');
			case 'POINTCLOUD_VIEW':
				return permissions.includes('pointcloud:read');
			case 'POINTCLOUD_UPLOAD':
				return permissions.includes('pointcloud:create');
			case 'POINTCLOUD_DELETE':
				return permissions.includes('pointcloud:delete');
			case 'POINTCLOUD_EDIT':
				return permissions.includes('pointcloud:update');
			case 'DSSMODULE':
				return permissions.includes('dssmodule:minimal');
			case 'IMAGEMODULE':
				return permissions.includes('imagemodule:minimal');
			case 'UPLOAD_IMAGES':
				return permissions.includes('imagemodule:create');
			case 'UPDATE_IMAGES':
				return permissions.includes('imagemodule:update');
			case 'DELETE_IMAGES':
				return permissions.includes('imagemodule:delete');
			default:
				return false;
		}
	}
};
